import { spaceAPI } from '@/api';
import HeaderComponent from './ResomHeader';
import { palette } from '@/modules/defines/styles';
import { useQuery } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
} from '@tanstack/react-table';
import type {
  ColumnDef,
  Row,
  Header,
  Cell,
  HeaderGroup,
} from '@tanstack/react-table';
import {
  Button,
  ButtonGroup,
  Container,
  Content,
  DeleteButton,
  HeaderContainer,
  LoadingMessage,
  Modal,
  ModalButtons,
  ModalContent,
  SearchContainer,
  SearchInput,
  SelectionCount,
  Table,
  TableContainer,
} from '@/components/CommonStyles';

interface ResomEvent {
  id: number;
  title: string;
  description: string;
  date: string;
  publish: boolean;
  img: string;
  link: string;
  updatedAt: string;
  space_id: number;
}

const ResomEventContainer = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const columnHelper = createColumnHelper<ResomEvent>();

  const { data: dataAPI, isLoading: isLoadingAPI } = useQuery({
    queryKey: ['getEventsResom'],
    queryFn: () =>
      spaceAPI.getEventsResom({
        code: import.meta.env.VITE_SECRET_API_KEY,
      }),
    staleTime: 1000 * 60 * 5,
    enabled: true,
  });

  const toggleRowSelection = (id: number) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const columns = useMemo(() => {
    const baseColumns = [
      isEditMode
        ? columnHelper.display({
            id: 'checkbox',
            header: () => (
              <input
                type="checkbox"
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedRows(
                      dataAPI?.data?.map((item: ResomEvent) => item.id) || []
                    );
                  } else {
                    setSelectedRows([]);
                  }
                }}
                checked={
                  selectedRows.length > 0 &&
                  selectedRows.length === (dataAPI?.data?.length || 0)
                }
              />
            ),
            cell: (info) => (
              <input
                type="checkbox"
                onChange={() => toggleRowSelection(info.row.original.id)}
                checked={selectedRows.includes(info.row.original.id)}
                onClick={(e) => e.stopPropagation()}
              />
            ),
          })
        : null,
      columnHelper.accessor('id', {
        header: 'ID',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('title', {
        header: '제목',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('description', {
        header: '설명',
        cell: (info) => {
          const text = info.getValue();
          return text.length > 10 ? `${text.slice(0, 10)}...` : text;
        },
      }),
      columnHelper.accessor('date', {
        header: '날짜',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('publish', {
        header: '공개 여부',
        cell: (info) => (info.getValue() ? '공개' : '비공개'),
      }),
      columnHelper.accessor('updatedAt', {
        header: '수정일',
        cell: (info) => new Date(info.getValue()).toLocaleDateString(),
      }),
    ];

    return baseColumns.filter(Boolean) as ColumnDef<ResomEvent, any>[];
  }, [isEditMode, selectedRows, dataAPI?.data]);

  const table = useReactTable({
    data: dataAPI?.data || [],
    columns,
    state: {
      sorting,
      globalFilter: searchTerm,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setSearchTerm,
    globalFilterFn: (row, columnId, filterValue) => {
      const value = String(row.getValue(columnId) || '').toLowerCase();
      return value.includes(String(filterValue).toLowerCase());
    },
  });

  const handleEditMode = () => {
    setIsEditMode(true);
    setSelectedRows([]);
  };

  const handleCancelEditMode = () => {
    setIsEditMode(false);
    setSelectedRows([]);
  };

  // 이벤트 생성 LOGIC
  const handleCreate = () => {
    const confirm = window.confirm('이벤트를 추가로 생성합니다.');
    if (confirm) {
      spaceAPI
        .createEventResom({
          code: import.meta.env.VITE_SECRET_API_KEY,
        })
        .then((res) => {
          console.log(res);
          if (res.status === 200) {
            alert(`이벤트가 생성되었습니다.\nid: ${res.data.id}`);
            window.location.href = `/resom/event/${res.data.id}`;
          } else {
            alert('이벤트 생성에 실패했습니다.');
          }
        });
    }
  };

  // 이벤트 삭제 LOGIC
  const handleDelete = () => {
    if (selectedRows.length === 0) {
      alert('삭제할 항목을 선택해주세요.');
      return;
    }
    setShowDeleteModal(true);
  };

  // 이벤트 삭제 확인 LOGIC
  const confirmDelete = () => {
    spaceAPI
      .deleteEventResom(
        {
          code: import.meta.env.VITE_SECRET_API_KEY,
        },
        { ids: selectedRows }
      )
      .then((res) => {
        console.log(res);
        if (res.status === 200) {
          alert('선택한 이벤트가 삭제되었습니다.');
          window.location.reload();
        } else {
          alert('이벤트 삭제에 실패했습니다.');
        }
      });

    setShowDeleteModal(false);
    setSelectedRows([]);
  };

  return (
    <Container>
      <HeaderComponent />
      <Content>
        {isLoadingAPI ? (
          <LoadingMessage>데이터를 불러오는 중입니다...</LoadingMessage>
        ) : !dataAPI?.data ? (
          <LoadingMessage>
            데이터가 없습니다. 잠시 후 다시 시도해주세요.
          </LoadingMessage>
        ) : (
          <>
            <HeaderContainer>
              <SearchContainer>
                <SearchInput
                  type="text"
                  placeholder="검색어를 입력하세요..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </SearchContainer>
              <ButtonGroup>
                {isEditMode ? (
                  <>
                    {selectedRows.length > 0 && (
                      <SelectionCount>
                        총 {selectedRows.length}개 선택
                      </SelectionCount>
                    )}
                    <Button onClick={handleCancelEditMode}>취소</Button>
                    <DeleteButton onClick={handleDelete}>삭제</DeleteButton>
                  </>
                ) : (
                  <>
                    <Button onClick={handleEditMode}>수정</Button>
                    <Button onClick={handleCreate}>생성</Button>
                  </>
                )}
              </ButtonGroup>
            </HeaderContainer>
            <TableContainer>
              <Table>
                <thead>
                  {table
                    .getHeaderGroups()
                    .map((headerGroup: HeaderGroup<ResomEvent>) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map(
                          (header: Header<ResomEvent, unknown>) => (
                            <th
                              key={header.id}
                              onClick={header.column.getToggleSortingHandler()}
                              style={{ cursor: 'pointer' }}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: ' 🔼',
                                desc: ' 🔽',
                              }[header.column.getIsSorted() as string] ?? null}
                            </th>
                          )
                        )}
                      </tr>
                    ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row: Row<ResomEvent>) => (
                    <tr
                      key={row.id}
                      onClick={
                        isEditMode
                          ? () => toggleRowSelection(row.original.id)
                          : () => navigate(`/resom/event/${row.original.id}`)
                      }
                      style={{ cursor: 'pointer' }}
                    >
                      {row
                        .getVisibleCells()
                        .map((cell: Cell<ResomEvent, unknown>) => (
                          <td key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          </>
        )}
        {showDeleteModal && (
          <Modal>
            <ModalContent>
              <p>{selectedRows.length}개의 항목이 선택되었습니다.</p>
              <h3>삭제하신 이벤트는 다시 복구할 수 없습니다.</h3>
              <h3>그래도 삭제하시겠습니까?</h3>
              <ModalButtons>
                <Button onClick={() => setShowDeleteModal(false)}>취소</Button>
                <DeleteButton onClick={confirmDelete}>삭제</DeleteButton>
              </ModalButtons>
            </ModalContent>
          </Modal>
        )}
      </Content>
    </Container>
  );
};

export default ResomEventContainer;
